import type { Affiliation } from '@interfaces/affiliation';
import { OrganizationUserAccessTypes } from '@interfaces/organizationUser';
import {
  Add,
  ArrowForwardIosSharp,
  CheckCircle,
  Edit,
  RemoveCircleOutline,
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import type { SxProps } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { useHelpTextTokens } from '@ovComponents/2-component/helpText/helpText.tokens';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { Menu } from '@ovComponents/2-component/menu/menu';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { ConvertAffiliate } from '@ovComponents/4-module/workflowCompletion/subSteps/editAccount/components/convertAffiliate';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { kebabCase } from 'lodash-es';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectionTileTokens } from '../selectionTile/selectionTile.tokens';

type CompletionBoxState = 'todo' | 'complete' | 'error' | 'next';
type CompletionBoxType = 'checkbox' | 'radio' | 'tile' | 'completion';

export const CompletionBox = ({
  state,
  title,
  titleSideComponent,
  onRemove,
  onAdd,
  onEdit,
  children,
  variant = 'edit',
  edittable = true,
  selected,
  infoTooltip,
  type,
  testId,
  error,
  errorText,
  affiliate,
  options,
  accountData,
  refetch,
  disabled = false,
  sx,
  innerSx,
}: {
  state: CompletionBoxState;
  title: string;
  titleSideComponent?: React.ReactNode;
  onRemove?: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  children?: any;
  variant?: 'edit' | 'view';
  edittable?: boolean;
  type?: CompletionBoxType;
  selected?: boolean;
  infoTooltip?: string;
  testId?: string;
  error?: boolean;
  errorText?: string;
  options?: any;
  accountData?: any;
  refetch?: () => void;
  affiliate?: Affiliation;
  disabled?: boolean;
  sx?: SxProps;
  innerSx?: SxProps;
}) => {
  const { comp, sys } = useThemeTokens(useSelectionTileTokens());
  const { comp: helpTextComp } = useThemeTokens(useHelpTextTokens());
  const { t } = useTranslation(['components']);
  const { permissions } = usePermissions();
  const { userContext } = useContext(UserContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openConvertAffiliate, setOpenConvertAffiliate] = useState(false);

  const shouldDisplayInDialog = helpTextComp.helpText.displayInDialog;

  const open = Boolean(anchorEl);

  // Invite user menu will visible only for OrganizationUserAccessTypes.ORGANIZATION users
  const canCovertAffiliate =
    affiliate &&
    permissions.includes('write:affiliate_conversion') &&
    affiliate?.user?.affiliateOnly &&
    userContext.accessType === OrganizationUserAccessTypes.ORGANIZATION &&
    options?.enableConvertedAffiiateOnboarding;

  const helpTextFunc = (isError?: boolean) => {
    if (isError) {
      return errorText;
    }
    return infoTooltip && !shouldDisplayInDialog ? infoTooltip : undefined;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const borderColor = () => {
    if (error) return sys.color.negative;
    if (variant === 'view') return undefined;
    switch (state) {
      case 'todo':
        return sys.color.outlineVariant;
      case 'complete':
        return sys.color.positive;
      case 'error':
        return sys.color.negative;
      default:
        return sys.color.outlineVariant;
    }
  };

  const displayMenuOptions =
    edittable && accountData && affiliate && ['complete'].includes(state);

  return (
    <>
      <Box
        data-testid={
          testId ? `${testId}-${kebabCase(title)}` : 'completion-box'
        }
        display='flex'
        borderRadius={sys.borderRadius.xl}
        border={`1px solid ${borderColor()}`}
        pt={1}
        pl={3}
        pr={2}
        pb={2}
        flexDirection='column'
        mb={2}
        sx={{
          transition: 'background-color 0.3s ease-in-out',
          cursor:
            (state === 'todo' || type === 'completion') && variant === 'edit'
              ? 'pointer'
              : 'default',
          '&:hover': {
            backgroundColor:
              (state === 'todo' || type === 'completion') && variant === 'edit'
                ? sys.color.background
                : 'default',
          },
          background: selected
            ? comp.selectionTile.hoverBackgroundColor
            : sys.color.surface,
          boxShadow: variant === 'view' ? sys.elevation.surface : undefined,
          ...sx,
        }}
        onClick={
          (state === 'todo' && variant === 'edit') || state === 'next'
            ? onAdd
            : undefined
        }
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          minHeight={40}
          sx={innerSx}
        >
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Box display='flex' flexDirection='row' alignItems={'center'}>
              <Typography
                data-testid={
                  testId
                    ? `${testId}-title-${kebabCase(title)}`
                    : `completion-box-title-${kebabCase(title)}`
                }
                variant='titleMedium'
                sx={{ mr: '4px' }}
              >
                {title}
                {infoTooltip && shouldDisplayInDialog && (
                  <InfoDialog information={infoTooltip} />
                )}
              </Typography>
              {titleSideComponent}
            </Box>
            {state === 'complete' && (
              <CheckCircle
                sx={{ color: sys.color.positive, fontSize: '16px', ml: 0.5 }}
              />
            )}
          </Box>
          <Box>
            {state === 'todo' && edittable && (
              <IconButton
                disableRipple={variant === 'edit'}
                onClick={variant === 'edit' ? undefined : onAdd}
                size='medium'
              >
                <Add
                  data-testid={testId ? `${testId}-add-button` : 'add-button'}
                  sx={{ color: sys.color.onSurfaceVariant }}
                />
              </IconButton>
            )}
            {!accountData && state === 'complete' && onEdit && edittable && (
              <IconButton onClick={onEdit} size='small' sx={{ height: '26px' }}>
                <Edit
                  data-testid={testId ? `${testId}-edit-button` : 'edit-button'}
                  sx={{ fontSize: '16px' }}
                />
              </IconButton>
            )}
            {!accountData && state === 'complete' && onRemove && edittable && (
              <IconButton
                onClick={onRemove}
                size='small'
                sx={{ height: '26px' }}
              >
                <RemoveCircleOutline
                  data-testid={
                    testId ? `${testId}-remove-button` : 'remove-button'
                  }
                  sx={{ fontSize: '16px' }}
                />
              </IconButton>
            )}
            {displayMenuOptions && (
              <IconButton
                sx={{ marginLeft: 1 }}
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(e);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
            {state === 'next' && (
              <ArrowForwardIosSharp
                data-testid={testId ? `${testId}-next-button` : 'next-button'}
                sx={{ fontSize: '16px', color: sys.color.onSurfaceVariant }}
              />
            )}
          </Box>
        </Box>
        {children}
      </Box>
      {helpTextFunc(error) && (
        <Box
          alignItems='center'
          display='flex'
          sx={{ position: 'relative', top: '-10px', mt: 0.5 }}
        >
          <HelpText
            text={helpTextFunc(error)}
            tone={error ? 'error' : 'neutral'}
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        id='affiliate-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {displayMenuOptions && onEdit && (
          <MenuItem
            data-testid={testId ? `${testId}-edit-button` : 'edit-button'}
            key='edit-menu-item'
            onClick={onEdit}
          >
            <IconButton size='small' sx={{ marginRight: 1 }}>
              <EditIcon />
            </IconButton>
            {t('affiliateConversion.edit')}
          </MenuItem>
        )}

        {displayMenuOptions && onRemove && (
          <MenuItem
            data-testid={testId ? `${testId}-remove-button` : 'remove-button'}
            key='delete-menu-item'
            onClick={onRemove}
          >
            <IconButton size='small' sx={{ marginRight: 1 }}>
              <DeleteIcon />
            </IconButton>
            {t('affiliateConversion.delete')}
          </MenuItem>
        )}

        {canCovertAffiliate && (
          <MenuItem
            key='invite'
            data-testid='affiliate-invite'
            onClick={() => {
              setOpenConvertAffiliate(true);
            }}
          >
            <IconButton size='small' sx={{ marginRight: 1 }}>
              <PersonIcon />
            </IconButton>
            {t('affiliateConversion.inviteUser')}
          </MenuItem>
        )}
      </Menu>
      {displayMenuOptions && (
        <ConvertAffiliate
          refetch={refetch}
          options={options}
          accountData={accountData}
          setOpenConvertAffiliate={setOpenConvertAffiliate}
          openConvertAffiliate={openConvertAffiliate}
          currentAffiliate={affiliate}
        />
      )}
    </>
  );
};
